import {
  BillingBusinessLicense,
  BillingChangeToPlan,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { formatInTimeZone } from 'date-fns-tz';
import { isEqual } from 'lodash';
import { TFunction } from 'react-i18next';
import { ResolvedPlanDetails } from './hooks/useSelectedPlanResolution';

export const ensureTrailingPeriod = (text?: string | null): string => {
  if (!text) {
    return '';
  }
  return /[.!?]$/.test(text) ? text : `${text}.`;
};

export const formatPendingDate = (date?: string) =>
  date ? formatInTimeZone(new Date(date), 'UTC', 'MM/dd/yyyy') : '';

type PlanChangeClarificationFields = Pick<
  BillingChangeToPlan,
  | 'plan_change_immediately'
  | 'has_pending_downgrades'
  | 'is_cadence_change'
  | 'new_billing_cycle'
  | 'new_billing_cycle_start_date'
>;

export const buildPlanChangeClarification = (
  fields: PlanChangeClarificationFields,
  t: TFunction
): string => {
  const {
    plan_change_immediately: hasImmediateChange,
    has_pending_downgrades: hasPendingDowngrades,
    is_cadence_change: isCadenceChange,
    new_billing_cycle: newBillingCycle,
    new_billing_cycle_start_date: newBillingCycleStartDate,
  } = fields;

  const date = formatPendingDate(newBillingCycleStartDate);

  if (isCadenceChange && hasImmediateChange) {
    const cadenceLabel =
      newBillingCycle === 'year' ? t('Annual') : t('Monthly');
    return hasPendingDowngrades
      ? t(
          'Plan upgrades apply immediately. {{cadence}} billing starts on {{date}}.',
          {
            cadence: cadenceLabel,
            date,
          }
        )
      : t('Plan changes immediately. {{cadence}} billing starts on {{date}}.', {
          cadence: cadenceLabel,
          date,
        });
  }

  const hasPendingChange = hasPendingDowngrades || isCadenceChange;

  if (hasImmediateChange && hasPendingChange) {
    return t('Plan upgrades apply immediately');
  }
  if (hasImmediateChange) {
    return t('Plan changes immediately');
  }

  return t('Plan changes at end of billing cycle on {{date}}', { date });
};

export type UnifiedPlan = {
  id: string;
  name: string;
  price: {
    amount: number;
    currency: string;
    symbol: string;
  };
  ui_config: any;
  config: any;
  annualDiscountPercentage: number;
};

export const MONTHS_PER_YEAR = 12;

/*
    Annual billing pricing (KZN-16871 / KZN-16872). Each plan applies its OWN annual discount
    percentage to its monthly price to get a discounted monthly rate; the annual rate is 12x that
    discounted monthly rate.

    Per-card annual prices are FE-computed (no per-card preview). Upcoming Plan Summary + Confirm
    modal totals use monthly_cost_summary.total from the change-to-plan preview, which is already
    cadence-aware (annual total when previewing billing_cycle: "year").
*/
export const getDiscountedMonthlyPrice = (
  monthlyAmount: number,
  annualDiscountPercentage: number
) => {
  const discountFactor = 1 - annualDiscountPercentage / 100;
  return monthlyAmount * discountFactor;
};

export const getAnnualPrice = (
  monthlyAmount: number,
  annualDiscountPercentage: number
) => {
  return (
    getDiscountedMonthlyPrice(monthlyAmount, annualDiscountPercentage) *
    MONTHS_PER_YEAR
  );
};

export const getAnnualTotalFromDiscountedMonthlies = (
  discountedMonthlies: number[]
) => {
  const sumDiscountedMonthly = discountedMonthlies.reduce(
    (sum, amount) => sum + amount,
    0
  );
  return Math.round(sumDiscountedMonthly * MONTHS_PER_YEAR * 100) / 100;
};

const maybeFormatValueAsNumber = (value: unknown) => {
  if (isNaN(Number(value))) {
    return String(value);
  } else {
    return Number(value).toLocaleString();
  }
};

const getValueForKey = (
  key: string,
  source: Record<string, unknown>
): string => {
  const parts = key.split('.');
  let value: any = source;

  for (const part of parts) {
    value = value?.[part];
  }

  return maybeFormatValueAsNumber(value);
};

/*
    The backend-driven billing CMS allows for interpolation in strings that are shown to the user. For example:
        "{limits.max_users} users included"
    The interpolatable values are anything defined under `config` on a plan (or unified plan from below).
*/
export const interpolateValues = (str: string, plan?: UnifiedPlan) => {
  const regex = /\{(.+?)\}/g;

  if (!plan) {
    return str;
  }

  if (regex.test(str)) {
    return str.replace(regex, (match, key) => {
      return getValueForKey(key, plan.config);
    });
  }

  return str;
};

/*
    Takes in slightly different representations of plans, and returns a unified structure for easier
    consumption. plan represents a license template, or an available, published plan. currentPlan is
    the currently active plan, which could be legacy or have a match with an existing published template.
    If a plan is provided, it will be preferred. 
*/
export const getUnifiedPlan = (
  plan?: BillingLicensePlans['license_templates'][number],
  currentPlan?: BillingBusinessLicense[number]
): UnifiedPlan | undefined => {
  if (!plan && !currentPlan) {
    return undefined;
  }

  if (plan) {
    return {
      id: plan.id,
      name: plan.published_name,
      price: plan.price,
      ui_config: plan.ui_config,
      config: plan.config,
      annualDiscountPercentage: plan.annual_discount_percentage ?? 0,
    };
  }

  if (currentPlan) {
    return {
      id: currentPlan.license_plan.id,
      name: currentPlan.license_plan.published_name,
      price: currentPlan.price,
      ui_config: currentPlan.ui_config,
      config: currentPlan.config,
      annualDiscountPercentage:
        currentPlan.license_plan.annual_discount_percentage ?? 0,
    };
  }

  return {
    id: '',
    name: '',
    price: {
      amount: 0,
      currency: '',
      symbol: '',
    },
    ui_config: {},
    config: {},
    annualDiscountPercentage: 0,
  };
};

export const isConfigSectionEqual = (
  comparisonPlanConfigSectionArg?: Record<string, unknown>,
  existingPlanConfigSectionArg?: Record<string, unknown>
) => {
  const { max_users: _, ...comparisonPlanConfigSection } =
    comparisonPlanConfigSectionArg || {};

  const { max_users: __, ...existingPlanConfigSection } =
    existingPlanConfigSectionArg || {};

  return isEqual(comparisonPlanConfigSection, existingPlanConfigSection);
};

/*
    Plans are considered equal if all of the following are true:
        1. They have the same plan category (i.e. general, automation, marketing)
        2. They have the same price and currency
        3. They have the same exact limits
        4. They have the same overage unit size and price

    Note: The backend does not enforce uniqueness across all these traits. That means
    it's possible for multiple plans to show as "equal" to each other. There's no good solution
    for this on the frontend, so if this behavior is encountered it means there are two
    functionally identical plans in the backend, which shouldn't happen and points to a config error.
*/
export const arePlansEqual = (
  comparisonPlan?:
    | BillingLicensePlans['license_templates'][number]
    | ResolvedPlanDetails,
  existingPlan?: BillingBusinessLicense[number]
) => {
  if (!existingPlan) {
    return false;
  }

  if (!comparisonPlan) {
    return false;
  }

  if (
    comparisonPlan.plan_category !== existingPlan.license_plan.plan_category
  ) {
    return false;
  }

  if (
    comparisonPlan.price?.amount !== existingPlan.price.amount ||
    comparisonPlan.price.currency !== existingPlan.price.currency
  ) {
    return false;
  }

  const limitsEqual = isConfigSectionEqual(
    comparisonPlan.config?.limits as Record<string, unknown>,
    existingPlan.config.limits as Record<string, unknown>
  );

  const overageEqual = isConfigSectionEqual(
    comparisonPlan.config?.overage as Record<string, unknown>,
    existingPlan.config.overage as Record<string, unknown>
  );

  return limitsEqual && overageEqual;
};
